import { useList } from '@refinedev/core';
import { FormField } from '@/components/forms';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { TriggerAction } from '../../types';

const ACCESS_TARGET_OPTIONS = [
  { value: 'COURSE', label: 'Курс' },
  { value: 'MODULE', label: 'Модуль' },
  { value: 'MODULE_ITEM', label: 'Элемент модуля' },
  { value: 'CHANNEL', label: 'Канал' },
  { value: 'CONTENT', label: 'Весь контент' },
] as const;

interface AccessFormProps {
  action: TriggerAction;
  onChange: (patch: Partial<TriggerAction>) => void;
}

/**
 * AccessForm
 * 
 * Форма действия управления доступом
 * 
 * @returns {React.ReactElement} React компонент
 */
export function AccessForm({ action, onChange }: AccessFormProps) {
  const isGrant = action.type === 'grant_access';

  const { result: coursesResult } = useList<{ id: string; slug: string; translations?: Array<{ title: string }> }>({
    resource: 'courses',
    pagination: { currentPage: 1, pageSize: 200, mode: 'server' },
  });

  const { result: channelsResult } = useList<{ id: string; title: string }>({
    resource: 'telegram-channels',
    pagination: { currentPage: 1, pageSize: 200, mode: 'server' },
  });

  const courses = coursesResult.data ?? [];
  const channels = channelsResult.data ?? [];

  const renderTargetSelect = () => {
    if (action.targetType === 'CONTENT') {return null;}

    if (action.targetType === 'COURSE') {
      return (
        <FormField label="Курс" name="targetId">
          <Select
            value={action.targetId ?? ''}
            onValueChange={(v) => onChange({ targetId: v || undefined })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Выберите курс" />
            </SelectTrigger>
            <SelectContent>
              {courses.map((course) => (
                <SelectItem key={course.id} value={course.id}>
                  {course.translations?.[0]?.title ?? course.slug ?? course.id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
      );
    }

    if (action.targetType === 'CHANNEL') {
      return (
        <FormField label="Канал" name="targetId">
          <Select
            value={action.targetId ?? ''}
            onValueChange={(v) => onChange({ targetId: v || undefined })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Выберите канал" />
            </SelectTrigger>
            <SelectContent>
              {channels.map((channel) => (
                <SelectItem key={channel.id} value={channel.id}>
                  {channel.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
      );
    }

    return (
      <FormField label="ID объекта" name="targetId">
        <Input
          value={action.targetId ?? ''}
          onChange={(e) => onChange({ targetId: e.target.value || undefined })}
          placeholder="UUID модуля или элемента"
        />
      </FormField>
    );
  };

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <FormField label="Тип доступа" name="targetType">
        <Select
          value={action.targetType ?? 'COURSE'}
          onValueChange={(v) =>
            onChange({ targetType: v as TriggerAction['targetType'], targetId: undefined })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ACCESS_TARGET_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>

      {renderTargetSelect()}

      {isGrant && (
        <FormField label="Срок доступа (дней)" name="durationDays" hint="Пусто = бессрочно">
          <Input
            type="number"
            min={0}
            value={action.durationDays ?? ''}
            onChange={(e) =>
              onChange({ durationDays: e.target.value ? Number(e.target.value) : undefined })
            }
            placeholder="30"
          />
        </FormField>
      )}
    </div>
  );
}
